import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../../services/permission.service';

export type PermissionInput = string | Array<string | { channel: string; resource: string; action: string }>;

@Directive({
  selector: '[hasPermission]'
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  @Input() hasPermission: PermissionInput;
  @Input() hasPermissionMode: 'any' | 'all' = 'any';
  @Input('mode') set mode(value: 'any' | 'all') {
    this.hasPermissionMode = value;
  }

  private subscription: Subscription = new Subscription();
  private hasView = false;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.updateView();
    this.subscription.add(
      this.permissionService.userPermissions$.subscribe(() => this.updateView())
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateView(): void {
    const hasAccess = this.checkPermission();
    if (hasAccess && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasAccess && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private checkPermission(): boolean {
    if (!this.hasPermission) return false;

    if (typeof this.hasPermission === 'string') {
      const parts = this.hasPermission.split(':');
      if (parts.length >= 3) {
        const [channel, resource, action] = parts;
        return this.permissionService.hasPermission(channel, resource, action);
      }
      return false;
    }

    if (Array.isArray(this.hasPermission)) {
      const normalized = this.hasPermission
        .map((p): { channel: string; resource: string; action: string } | null => {
          if (typeof p === 'string') {
            const parts = p.split(':');
            if (parts.length >= 3) {
              return { channel: parts[0], resource: parts[1], action: parts[2] };
            }
            return null;
          }
          if (p && typeof p === 'object' && 'channel' in p && 'resource' in p && 'action' in p) {
            return p as { channel: string; resource: string; action: string };
          }
          return null;
        })
        .filter((p): p is { channel: string; resource: string; action: string } => !!p);

      if (normalized.length === 0) return false;

      const check = (n: { channel: string; resource: string; action: string }) =>
        this.permissionService.hasPermission(n.channel, n.resource, n.action);

      if (this.hasPermissionMode === 'all') {
        return normalized.every(check);
      }
      return normalized.some(check);
    }

    return false;
  }
}
